import {
  AlertCircle,
  CheckCircle2,
  ExternalLink,
  Eye,
  EyeOff,
  KeyRound,
  Loader2,
  LockKeyhole,
  RefreshCw,
} from 'lucide-react';
import { useState } from 'react';

import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { AssetTransferCreds } from '@/services/tokenTransfer';
import { isValidAddressForChain } from '@/utils/chains';

export type OpStatus = {
  type: 'success' | 'error' | null;
  message: string;
  txHash?: string;
  extra?: string;
};

export type AssetNav = {
  assetId: string | number;
  name: string;
  nav: number;
  navLastUpdated: string;
  tokenAddress: string;
  symbol: string;
  decimals: number;
};

export const SectionLabel = ({ children }: { children: React.ReactNode }) => (
  <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground pt-1">
    {children}
  </p>
);

export const FieldRow = ({
  id,
  label,
  optional,
  children,
}: {
  id: string;
  label: string;
  optional?: boolean;
  children: React.ReactNode;
}) => (
  <div className="space-y-1.5">
    <Label htmlFor={id}>
      {label}
      {optional && (
        <span className="ml-1 text-muted-foreground font-normal text-xs">
          (optional)
        </span>
      )}
    </Label>
    {children}
  </div>
);

export const StatusBanner = ({ status }: { status: OpStatus }) => {
  if (!status.type) return null;
  const ok = status.type === 'success';
  return (
    <div
      className={`rounded-md px-3 py-2.5 text-sm border ${ok ? 'bg-green-50 text-green-700 border-green-200' : 'bg-red-50 text-red-700 border-red-200'}`}
    >
      <div className="flex items-start gap-2">
        {ok ? (
          <CheckCircle2 className="h-4 w-4 mt-0.5 shrink-0" />
        ) : (
          <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
        )}
        <span>{status.message}</span>
      </div>
      {ok && status.txHash && (
        <div className="mt-2 ml-6">
          {process.env.REACT_APP_BLOCKCHAIN_EXPLORER_URL ? (
            <a
              href={`${process.env.REACT_APP_BLOCKCHAIN_EXPLORER_URL.replace(/\/$/, '')}/tx/${status.txHash}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 rounded-md border border-green-300 bg-white px-3 py-1.5 text-xs font-medium text-green-700 hover:bg-green-50 cursor-pointer"
            >
              <ExternalLink size={11} />
              View on Explorer
            </a>
          ) : (
            <p className="font-mono text-xs text-green-600 break-all">
              {status.txHash}
            </p>
          )}
        </div>
      )}
      {ok && status.extra && (
        <p className="mt-0.5 ml-6 text-xs text-green-600">{status.extra}</p>
      )}
    </div>
  );
};

export const WalletBanner = ({
  label,
  address,
  balance,
  balanceUnit,
  balanceLoading,
  onRefresh,
}: {
  label: string;
  address: string;
  balance: string | null;
  balanceUnit: string;
  balanceLoading: boolean;
  onRefresh: () => void;
}) => {
  const explorerBase = process.env.REACT_APP_BLOCKCHAIN_EXPLORER_URL?.replace(
    /\/$/,
    '',
  );
  return (
    <Card className="bg-muted/30">
      <CardContent className="py-3 px-4">
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            <p className="text-xs text-muted-foreground mb-0.5">{label}</p>
            <p className="font-mono text-sm break-all">{address}</p>
            <div className="flex items-center gap-1.5 mt-1.5">
              <p className="text-xs text-muted-foreground">Balance:</p>
              {balanceLoading ? (
                <Loader2
                  size={12}
                  className="animate-spin text-muted-foreground"
                />
              ) : balance !== null ? (
                <p className="text-xs font-medium">
                  {balance} {balanceUnit}
                </p>
              ) : (
                <p className="text-xs text-muted-foreground">unavailable</p>
              )}
              <button
                onClick={onRefresh}
                disabled={balanceLoading}
                className="text-muted-foreground hover:text-foreground disabled:opacity-40"
                aria-label="Refresh"
              >
                <RefreshCw size={11} />
              </button>
            </div>
          </div>
          {explorerBase && (
            <a
              href={`${explorerBase}/address/${address}`}
              target="_blank"
              rel="noopener noreferrer"
              className="shrink-0"
            >
              <Button variant="ghost" size="sm">
                <ExternalLink size={14} className="mr-1.5" />
                Explorer
              </Button>
            </a>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export const SessionBar = ({
  credentials,
  onLock,
}: {
  credentials: AssetTransferCreds;
  onLock: () => void;
}) => (
  <div className="flex items-center justify-between rounded-md border px-4 py-2.5 bg-muted/40">
    <div className="flex items-center gap-2 text-sm text-green-600">
      <CheckCircle2 size={15} />
      <span>
        Authenticated &mdash;{' '}
        <span className="font-mono text-xs text-muted-foreground">
          {credentials.organizationId.slice(0, 8)}…
        </span>
      </span>
    </div>
    <Button variant="outline" size="sm" onClick={onLock}>
      <LockKeyhole size={14} className="mr-1.5" />
      Lock
    </Button>
  </div>
);

/**
 * Wallet operations run against the chain the panel is configured for.
 *
 * A constant rather than a picker: these are break-glass operations against one deployment's
 * contracts, and every chain in the registry is EVM today, so the validation is the same for
 * all of them. When a non-EVM chain arrives this becomes a selection, and the check below is
 * already written to follow it.
 */
export const OPS_CHAIN = 'AVALANCHE_FUJI';

/**
 * The Turnkey org and wallets for this deployment, from its env file: Fuji wallets in `.env`
 * (dev), mainnet wallets in `.env.prod`. A field set here is locked in the form, so a dev
 * deployment cannot be pointed at the mainnet treasury, nor the reverse.
 *
 * Public values only — every REACT_APP_* variable ships in the bundle. The Turnkey API key
 * pair is a secret and is always typed in, never configured.
 */
export const OPS_ENV = {
  organizationId: process.env.REACT_APP_TURNKEY_ORG_ID?.trim() ?? '',
  walletAddress: process.env.REACT_APP_TREASURY_WALLET_ADDRESS?.trim() ?? '',
  relayerAddress: process.env.REACT_APP_RELAYER_WALLET_ADDRESS?.trim() ?? '',
};

export const CredentialGate = ({
  onAuthenticate,
}: {
  onAuthenticate: (creds: AssetTransferCreds) => void;
}) => {
  const [form, setForm] = useState({
    organizationId: OPS_ENV.organizationId,
    walletAddress: OPS_ENV.walletAddress,
    relayerAddress: OPS_ENV.relayerAddress,
    apiPublicKey: '',
    apiPrivateKey: '',
    creWebhookUrl: '',
  });
  const [showPrivateKey, setShowPrivateKey] = useState(false);
  const [error, setError] = useState('');

  const set = (field: keyof typeof form, value: string) =>
    setForm((p) => ({ ...p, [field]: value }));

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const {
      organizationId,
      walletAddress,
      relayerAddress,
      apiPublicKey,
      apiPrivateKey,
    } = form;
    if (
      !organizationId ||
      !walletAddress ||
      !relayerAddress ||
      !apiPublicKey ||
      !apiPrivateKey
    ) {
      setError(
        'Organization ID, both wallet addresses, and API key pair are required.',
      );
      return;
    }
    // Format-checked against the chain family before anything is signed. These operations
    // move real tokens, and an address that is malformed — or valid on a different family —
    // fails after the signature rather than before it, which is the expensive place to find
    // out. The check is by family, not network, because one keypair serves every EVM chain.
    const badAddress = [
      ['Wallet address', walletAddress],
      ['Relayer address', relayerAddress],
    ].find(([, value]) => !isValidAddressForChain(OPS_CHAIN, value));
    if (badAddress) {
      setError(`${badAddress[0]} is not a valid address for this chain.`);
      return;
    }
    setError('');
    onAuthenticate(form);
  };

  const inputRow = (
    id: string,
    label: string,
    placeholder: string,
    field: keyof typeof form,
    opts?: { optional?: boolean },
  ) => {
    const locked = !!OPS_ENV[field as keyof typeof OPS_ENV];
    return (
      <div className="space-y-1.5">
        <Label htmlFor={id}>
          {label}
          {opts?.optional && (
            <span className="ml-1 text-muted-foreground font-normal text-xs">
              (optional)
            </span>
          )}
          {locked && (
            <span className="ml-1 text-muted-foreground font-normal text-xs">
              (set for this environment)
            </span>
          )}
        </Label>
        <Input
          id={id}
          placeholder={placeholder}
          value={form[field]}
          readOnly={locked}
          className={locked ? 'bg-muted/50 font-mono text-xs' : undefined}
          onChange={(e) => set(field, e.target.value)}
        />
      </div>
    );
  };

  return (
    <Card className="max-w-md mx-auto mt-6">
      <CardHeader>
        <div className="flex items-center gap-2">
          <KeyRound className="h-5 w-5 text-muted-foreground" />
          <CardTitle className="text-lg">Turnkey Credentials</CardTitle>
        </div>
        <CardDescription>
          All operations are signed and broadcast directly from your browser.
          Nothing is sent to the backend — credentials are never stored.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-4">
          <SectionLabel>Turnkey API</SectionLabel>
          {inputRow(
            'org-id',
            'Organization ID',
            'xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx',
            'organizationId',
          )}

          <div className="space-y-1.5">
            <Label htmlFor="priv-key">API Private Key</Label>
            <div className="relative">
              <Input
                id="priv-key"
                type={showPrivateKey ? 'text' : 'password'}
                placeholder="Ed25519 private key (hex)"
                value={form.apiPrivateKey}
                onChange={(e) => set('apiPrivateKey', e.target.value)}
                className="pr-10"
              />
              <button
                type="button"
                onClick={() => setShowPrivateKey((v) => !v)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                aria-label={showPrivateKey ? 'Hide' : 'Show'}
              >
                {showPrivateKey ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
          </div>

          {inputRow(
            'pub-key',
            'API Public Key',
            'Ed25519 public key (hex)',
            'apiPublicKey',
          )}

          <SectionLabel>Wallets</SectionLabel>
          {inputRow(
            'treasury',
            'Treasury Wallet Address',
            '0x... (token owner, authorizes transfers)',
            'walletAddress',
          )}
          {inputRow(
            'relayer',
            'Relayer Wallet Address',
            '0x... (pays gas, submits transactions)',
            'relayerAddress',
          )}

          <SectionLabel>Integrations</SectionLabel>
          {inputRow(
            'cre',
            'CRE Webhook URL',
            'https://... (cap table sync)',
            'creWebhookUrl',
            { optional: true },
          )}

          {error && (
            <p className="text-sm text-destructive flex items-center gap-1.5">
              <AlertCircle size={13} />
              {error}
            </p>
          )}

          <Button
            type="submit"
            className="w-full"
            disabled={
              !form.organizationId ||
              !form.walletAddress ||
              !form.relayerAddress ||
              !form.apiPublicKey ||
              !form.apiPrivateKey
            }
          >
            Unlock Wallet Operations
          </Button>
        </form>
      </CardContent>
    </Card>
  );
};
